import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Store Availability Component
 * Handles the expand/collapse functionality for store availability details
 * and updates when variant changes
 */
class StoreAvailabilityComponent extends Component {
  requiredRefs = ['toggleButton', 'detailsContent'];

  connectedCallback() {
    super.connectedCallback();
    
    // Listen for variant updates
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }
  }

  /**
   * Handles variant update events
   * @param {VariantUpdateEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail.resource;
    if (variant && variant.id) {
      this.dataset.variantId = variant.id;
      
      // Close details if open when variant changes
      if (this.dataset.expanded === 'true') {
        this.toggleDetails();
      }
      
      // Re-fetch store availability data
      this.#updateStoreAvailability(variant);
    }
  };

  /**
   * Updates store availability based on new variant
   * @param {Object} variant - The variant object
   */
  async #updateStoreAvailability(variant) {
    // The store availability data should be updated via server-side rendering
    // This method can be extended if needed for client-side updates
  }

  /**
   * Toggles the details section visibility
   */
  toggleDetails = () => {
    const { toggleButton, detailsContent } = this.refs;
    if (!toggleButton || !detailsContent) return;

    const container = this.querySelector('.store-availability__container');
    if (!container) return;

    const isExpanded = container.hasAttribute('data-expanded') && container.dataset.expanded === 'true';
    
    if (isExpanded) {
      // Collapse
      detailsContent.hidden = true;
      toggleButton.setAttribute('aria-expanded', 'false');
      container.removeAttribute('data-expanded');
    } else {
      // Expand
      detailsContent.hidden = false;
      toggleButton.setAttribute('aria-expanded', 'true');
      container.setAttribute('data-expanded', 'true');
    }
  };
}

if (!customElements.get('store-availability-component')) {
  customElements.define('store-availability-component', StoreAvailabilityComponent);
}
